// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Decimal } from 'decimal.js';
class _MathUtils {
    constructor() {
        this.regexpPrecision = /(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;
        this.MATH_ROUND_EPSILON = 0.000000001;
        // Polyfill.
        this.trunc = Math.trunc ||
            function (x) {
                return x < 0 ? Math.ceil(x) : Math.floor(x);
            };
        this.EPS_ROUNDING_REGEXP = /0+$/;
        this.EpsTableFormat = {};
    }
    TruncateDouble(value, signCount) {
        if (!value) {
            return 0;
        } // -0 fix #98014
        const factor = Math.pow(10, signCount);
        const epsilon = value > 0 ? MathUtils.MATH_ROUND_EPSILON : -MathUtils.MATH_ROUND_EPSILON; // #85860
        return MathUtils.trunc(((value * 10 + epsilon) * factor) / 10) / factor; // #85574 //#85860
    }
    // WARNING. May cause minor precision errors.
    // отбрасывание до инкремента
    RoundToIncrement(value, roundToValue) {
        if (roundToValue <= 0) {
            return value;
        }
        // +hsa - на сервере используют математическое округление
        return Math.round(value / roundToValue + MathUtils.MATH_ROUND_EPSILON) * roundToValue;
    }
    RoundTo(number, roundTo) {
        if (roundTo === 0) {
            return number;
        }
        if (number === 0) {
            return number;
        }
        const rounded = Math.round(number / roundTo) * roundTo;
        return Math.abs(rounded) >= roundTo ? rounded : roundTo;
    }
    RoundDouble(value, signCount) {
        if (!value) {
            return 0;
        } // -0 fix #98014
        const factor = Math.pow(10, signCount);
        const epsilon = value > 0 ? MathUtils.MATH_ROUND_EPSILON : -MathUtils.MATH_ROUND_EPSILON; // #85860
        return Math.round(((value * 10 + epsilon) * factor) / 10) / factor; // #85574 //#85860
    }
    Round(value, decimalPlaces) {
        const f = Math.pow(10, decimalPlaces);
        return Math.round(value * f) / f;
    }
    CeilToDecimalPlaces(value, decimalPlaces) {
        const f = Math.pow(10, decimalPlaces);
        return Math.ceil(value * f) / f;
    }
    FloorToDecimalPlaces(value, decimalPlaces) {
        const f = Math.pow(10, decimalPlaces);
        return Math.floor(value * f) / f;
    }
    equalToEpsilon(value1, value2) {
        // проверяет равны ли величины с точностью до MathUtils.MATH_ROUND_EPSILON = 0.000000001
        return Math.abs(value1 - value2) < MathUtils.MATH_ROUND_EPSILON;
    }
    GetValuePrecision(value, precisionIncr, minPrecision) {
        if (value === 0 || isNaN(value) || Math.abs(value) < MathUtils.MATH_ROUND_EPSILON) {
            return 0;
        }
        value = Math.abs(value);
        let precision = Math.log10(value);
        if (precision > 0) {
            const fraction = value % Math.floor(value);
            if (fraction !== 0) {
                precision = Math.abs(Math.log10(fraction)) + precisionIncr;
            }
            else {
                precision = 0;
            }
        }
        else {
            precision = Math.abs(precision) + precisionIncr;
        }
        return minPrecision !== -1 && minPrecision >= precision ? minPrecision : Math.floor(precision);
    }
    /**
     * Determines the number of digits after the decimal separator.
     *
     * This is a TypeScript analogue of the .NET method `Utils.GetValuePrecision(decimal value)`.
     * It returns the number of decimal places in the given numeric value.
     *
     * @param value - The number to analyze.
     * @returns The number of digits after the decimal point.
     */
    getValuePrecision(value) {
        const decimal = new Decimal(value);
        if (decimal.isInteger()) {
            return 0;
        }
        return decimal.decimalPlaces();
    }
    // http://stackoverflow.com/questions/10454518/javascript-how-to-retrieve-the-number-of-decimals-of-a-string-number
    getPrecision(value) {
        const match = ('' + value).match(MathUtils.regexpPrecision);
        if (!match) {
            return 0;
        }
        return Math.max(0, 
        // Number of digits right of decimal point.
        (match[1] ? match[1].length : 0) -
            // Adjust for scientific notation.
            (match[2] ? +match[2] : 0));
    }
    /**
     * The method returns the remainder of the division with increased precision.
     * This is because the regular division remainder (%) does not always return data correctly.
     * For example, 1 % 0.01 === 0.00999999999999998 in JavaScript.
     * This method returns 0
     * @param {number} dividend - The number to be divided.
     * @param {number} divisor - The number by which the dividend is to be divided.
     * @returns {number} Remainder of the division
     */
    mod(dividend, divisor) {
        const dividendDecimal = new Decimal(dividend);
        const divisorDecimal = new Decimal(divisor);
        const remainder = dividendDecimal.mod(divisorDecimal);
        return remainder.toNumber();
    }
    IsNullOrUndefined(o) {
        return o === null || o === undefined;
    }
    // Получить котичество знаков после точки
    GetDecimalPlaces(value, max = undefined) {
        // вероятно стоит отказаться от использования - performance test показал что этот метод на ~35% менее эффективный в сравнении с MathUtils.getPrecision (на случайных числах десятичной записи от 0 до 8 точек после запятой)
        if (MathUtils.IsNullOrUndefined(value)) {
            return null;
        }
        const str = value.toString();
        let spl = str.split('.');
        let places = 0;
        if (spl.length > 1) {
            places = spl[1].length;
        }
        else {
            spl = str.split('e-');
            if (spl.length > 1) {
                places = parseInt(spl[1]);
            }
        }
        return places;
    }
    formatValueWithEps(numb) {
        if (!numb) {
            return '0';
        }
        let res = numb.toString();
        if (res.indexOf('e') !== -1) {
            res = MathUtils.EpsTableFormat[numb] || numb.toFixed(20).replace(MathUtils.EPS_ROUNDING_REGEXP, '');
            if (!MathUtils.EpsTableFormat[numb]) {
                MathUtils.EpsTableFormat[numb] = res;
            }
        }
        return res;
    }
    ProcessNaN(value, defaultValue = undefined) {
        defaultValue = defaultValue || 0;
        return value === null || isNaN(value) ? defaultValue : value;
        // return !value.HasValue || double.IsNaN(value.Value) ? defaultValue : value.Value;
    }
    isValueMultipleToStep(value, step = undefined, stepPrecision = undefined) {
        const curValuePrecision = this.getPrecision(value);
        if (curValuePrecision > stepPrecision)
            return false;
        const multiplier = Math.pow(10, stepPrecision);
        const intValue = Math.round(value * multiplier);
        const intStep = Math.round(step * multiplier);
        return intValue % intStep === 0;
    }
    random(min, max) {
        return Math.floor(Math.random() * (max - min)) + min;
    }
    gcd(a, b) {
        return b === 0 ? a : this.gcd(b, a % b);
    }
    lcm(a, b) {
        return (a * b) / this.gcd(a, b);
    }
}
// TODO
Math.log10 =
    Math.log10 ||
        function (x) {
            return Math.log(x) * Math.LOG10E;
        };
Math.trunc =
    Math.trunc ||
        function (x) {
            if (isNaN(x)) {
                return NaN;
            }
            if (x > 0) {
                return Math.floor(x);
            }
            return Math.ceil(x);
        };
export const DECIMAL_SEPARATOR = (() => {
    const tmp = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return tmp.format(1).charAt(1);
})();
export const GROUPING_SEPARATOR = (() => {
    var _a, _b;
    const number = 1000;
    const formatted = new Intl.NumberFormat(undefined).format(number);
    const separator = (_b = (_a = formatted.match(/[^0-9]/)) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '';
    return separator;
})();
export const MathUtils = new _MathUtils();
